import useSnackbarStore from '../store/snackbar';
import silentEndpoints from '@/config/silentEndpoints';

/**
 * api:
 * This function is used to handle fetching data from the server
 * It takes the request and the options for the fetch
 * It returns the data and the error
 *
 * Using $fetch to make network requests based on user interaction.
 *
 * @param {NitroFetchRequest} request: The request to fetch the data
 * @param {NitroFetchOptions} opts: The options for the fetch
 * @returns {pending, data, error, execute}
 */
export default defineNuxtPlugin(() => {
  const snackbar = useSnackbarStore();
  const requestHeaders: HeadersInit = new Headers();
  requestHeaders.set('Content-Type', 'application/json; charset=utf-8');

  const $api = $fetch.create({
    baseURL: useRuntimeConfig().public.apiURL,
    headers: {
      Accept: 'application/json',
    },
    onRequest({ request, options, error }) {
      const accessToken = useCookie(useRuntimeConfig().public.auth?.provider?.token?.cookieName || 'accessToken');
      if (accessToken.value) {
        options.headers = new Headers(options.headers || {});
        options.headers.set('Authorization', `Bearer ${accessToken.value}`);
      }
    },
    async onResponseError({ response, request }) {
      // Don't show snackbar for certain endpoints that might fail silently
      const isSilentEndpoint = silentEndpoints.some(endpoint => request.toString().includes(endpoint));

      // Don't show snackbar for blocked account errors (will be handled by dialog)
      const isBlockedAccount = response._data?.error_code === 'ACCOUNT_BLOCKED' || response._data?.is_blocked === true;

      if (!isSilentEndpoint && !isBlockedAccount) {
        snackbar.showSnackbar({
          text: `${response._data?.message}` || 'An error occurred',
          state: false,
        });
      }

      // Handle authentication errors
      if (response.status === 401 || response.status === 403) {
        console.warn('Authentication error:', response._data?.message);
        // You might want to redirect to login or refresh token here
      }
    },
  });

  return {
    provide: {
      api: $api,
    },
  };
});
